import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type {
  CsvAnalyzeResponse,
  CsvParseResponse,
  LicenceResponse,
  ListResponse,
  ProfileResponse,
} from '@shared/schemas';

import type { FilterCombinator, FilterGroup, FilterRule } from '@/types/filters';

import { EMPTY_ENRICHMENT_STATS, type EnrichmentStats } from '../../_shared/domain/enrichment';
import { toggleOverwriteRow } from '../../_shared/domain/file-import';
import {
  applyAnalyzeAcknowledged,
  applyAnalyzeFailed,
  applyAnalyzeStarted,
  applyAnalyzeSucceeded,
  applyMappingChanged,
  applyParseFailed,
  applyParseStarted,
  applyParseSucceeded,
  applyStepBack,
  type FileImportData,
  initialFileUploadState,
} from '../../_shared/domain/file-import.model';
import {
  addGroup,
  addRule,
  clearTree,
  loadTree,
  moveNode,
  removeNode,
  setCombinator,
  updateRule,
} from '../../_shared/domain/filter-tree';
import {
  applyImportCleared,
  applyImportedProfileReplaced,
  applyImportExclusionToggled,
  applyImportPreviewLoaded,
  applyImportPreviewLoading,
  applyImportPreviewPageChanged,
  applyImportPreviewPageSizeChanged,
  applyImportSucceeded,
} from '../../_shared/domain/list-import.model';
import { applyMethodsChanged } from '../../_shared/domain/method-scope';
import {
  followSuggestion,
  joinListName,
  nextListNumber,
  suggestedNameParts,
} from '../../_shared/domain/naming-convention';
import type { SourceListOrigin } from '../../_shared/infrastructure/list-import-query';
import type { AddListError, ListDraft, ListMethod } from '../domain/list-draft.model';
import {
  EMPTY_SELECTION,
  type RowSelection,
  selectAllResults,
  setPageSelected,
  toggleRow,
} from '../domain/row-selection';
import { type AddListState, initialState } from './add-list.state';

/**
 * Fill the parts of the name the form knows (number, licence, month) while
 * the admin has not made them their own: each part follows the suggestion while
 * it is empty or still the last suggested value, and stays put from the first
 * keystroke in it. The purpose is theirs from the start (#595, board E5).
 */
function refreshNameSuggestion(state: AddListState) {
  const names = state.licenceListNames;
  if (names === null || !state.draft.licenceName) return;
  const next = joinListName(
    suggestedNameParts({
      number: nextListNumber(names),
      clientName: state.draft.licenceName,
      startDate: state.draft.startDate,
    }),
  );
  const name = followSuggestion(state.draft.name, state.nameSuggestion, next);
  if (name !== state.draft.name) {
    state.draft.name = name;
    delete state.validationErrors.name;
  }
  state.nameSuggestion = next;
}

/**
 * Ad List slice. Reducers are pure state transitions only; every side effect
 * (validation trigger, API calls) lives in the saga, which dispatches the
 * `save*` transitions below as it progresses.
 */
const slice = createSlice({
  name: 'addList',
  initialState,
  reducers: {
    /** Hydrate the draft from a persisted source (localStorage / query params). */
    draftHydrated: (state, action: PayloadAction<Partial<ListDraft>>) => {
      state.draft = { ...state.draft, ...action.payload };
    },
    /** A single text field changed; clears that field's validation error. */
    fieldChanged: (
      state,
      action: PayloadAction<{ field: keyof ListDraft; value: string }>,
    ) => {
      const { field, value } = action.payload;
      if (field === 'methods') return; // methods are set via methodsChanged
      state.draft[field] = value;
      delete state.validationErrors[field];
      // Another licence's lists are another numbering: wait for them.
      if (field === 'licenceId') state.licenceListNames = null;
      if (field === 'licenceName' || field === 'startDate') refreshNameSuggestion(state);
    },
    /**
     * Change the selected methods AND drop what the unselected ones owned: an
     * uploaded file, imported ids, a filter tree. Leaving them behind is how a
     * previous step silently populated the next list (docs/product-issues.md
     * PI-40); the UI confirms the loss before dispatching this.
     */
    methodsChanged: (state, action: PayloadAction<ListMethod[]>) => {
      applyMethodsChanged(state, state.draft.methods, action.payload);
      if (!action.payload.includes('select')) state.rowSelection = EMPTY_SELECTION;
      state.draft.methods = action.payload;
      delete state.validationErrors.methods;
    },
    stepChanged: (state, action: PayloadAction<number>) => {
      state.step = action.payload;
    },
    // ── Save lifecycle (dispatched by the saga) ──
    saveStarted: (state) => {
      state.saveStatus = 'saving';
      state.error = null;
      state.validationErrors = {};
    },
    savePhaseSet: (state, action: PayloadAction<string>) => {
      state.savePhase = action.payload;
    },
    saveSucceeded: (state, action: PayloadAction<string>) => {
      state.saveStatus = 'succeeded';
      state.createdListId = action.payload;
      state.savePhase = '';
    },
    saveFailed: (state, action: PayloadAction<AddListError>) => {
      state.saveStatus = 'failed';
      state.error = action.payload;
      state.savePhase = '';
      if (action.payload.kind === 'validation') {
        state.validationErrors = action.payload.fields;
      }
    },
    // ── Selection ──
    /** Restore the persisted selection fields on mount (localStorage draft). */
    selectionHydrated: (
      state,
      action: PayloadAction<{
        excludeDuplicates?: boolean;
        importedProfileIds?: string[];
        importedListName?: string;
        rawImportedIds?: string[];
        duplicateIds?: string[];
        importExcludeDuplicates?: boolean;
      }>,
    ) => {
      const {
        excludeDuplicates,
        importedProfileIds,
        importedListName,
        rawImportedIds,
        duplicateIds,
        importExcludeDuplicates,
      } = action.payload;
      if (excludeDuplicates !== undefined) state.excludeDuplicates = excludeDuplicates;
      if (importedProfileIds !== undefined) state.importedProfileIds = importedProfileIds;
      if (importedListName !== undefined) state.importedListName = importedListName;
      // Without these the reloaded import step shows "0 profiles" and its
      // exclusion toggle no-ops (docs/product-issues.md PI-64).
      if (rawImportedIds !== undefined) state.listImport.rawImportedIds = rawImportedIds;
      if (duplicateIds !== undefined) state.listImport.duplicateIds = duplicateIds;
      if (importExcludeDuplicates !== undefined) {
        state.listImport.excludeDuplicates = importExcludeDuplicates;
      }
    },
    excludeDuplicatesToggled: (state) => {
      state.excludeDuplicates = !state.excludeDuplicates;
      // Other results: the ticks made against the previous ones no longer hold.
      state.rowSelection = EMPTY_SELECTION;
    },
    importSet: (state, action: PayloadAction<{ ids: string[]; listName: string }>) => {
      state.importedProfileIds = action.payload.ids;
      state.importedListName = action.payload.listName;
    },
    importCleared: (state) => {
      applyImportCleared(state);
    },

    // ── "Import from existing list" step (shared transitions with Edit) ──
    sourceOriginChanged: (state, action: PayloadAction<SourceListOrigin>) => {
      state.listImport.origin = action.payload;
    },
    sourceLicenceChanged: (state, action: PayloadAction<string>) => {
      state.listImport.sourceLicenceId = action.payload;
    },
    sourceListsLoadStarted: (state) => {
      state.listImport.sourceListsLoading = true;
    },
    /** Also the failure transition; a failed load lands an empty dropdown. */
    sourceListsLoaded: (state, action: PayloadAction<ListResponse[]>) => {
      state.listImport.sourceLists = action.payload;
      state.listImport.sourceListsLoading = false;
    },
    listImportStarted: (state) => {
      state.listImport.importing = true;
    },
    listImportSucceeded: (
      state,
      action: PayloadAction<{ rawIds: string[]; duplicateIds: string[]; listName: string }>,
    ) => {
      applyImportSucceeded(state, action.payload);
    },
    /** Silent failure; the button just re-enables, as the old step did. */
    listImportFailed: (state) => {
      state.listImport.importing = false;
    },
    importExcludeDuplicatesToggled: (state) => {
      applyImportExclusionToggled(state);
    },
    /** An imported profile was merged into another from the drawer (#746). */
    importedProfileReplaced: (state, action: PayloadAction<{ fromId: string; toId: string }>) => {
      applyImportedProfileReplaced(state, action.payload);
    },

    // ── Imported-profiles preview table (driven by the fetch saga) ──
    importPreviewLoading: (state) => {
      applyImportPreviewLoading(state.listImport);
    },
    importPreviewLoaded: (
      state,
      action: PayloadAction<{ rows: ProfileResponse[]; total: number }>,
    ) => {
      applyImportPreviewLoaded(state.listImport, action.payload);
    },
    importPreviewPageChanged: (state, action: PayloadAction<number>) => {
      applyImportPreviewPageChanged(state.listImport, action.payload);
    },
    importPreviewPageSizeChanged: (state, action: PayloadAction<number>) => {
      applyImportPreviewPageSizeChanged(state.listImport, action.payload);
    },

    fileImportSet: (state, action: PayloadAction<FileImportData | null>) => {
      state.fileImportData = action.payload;
    },

    // ── Embedded file-import flow (delegates to the pure _shared transitions) ──
    fileParseStarted: (state, action: PayloadAction<{ fileName: string; fileSize?: number }>) => {
      state.fileUpload = applyParseStarted(state.fileUpload, action.payload.fileName);
      state.fileSize = action.payload.fileSize ?? null;
      state.fileProgress =
        action.payload.fileSize === undefined
          ? null
          : { loaded: 0, total: action.payload.fileSize };
      state.error = null;
    },
    /** Bytes sent so far (the upload's own progress). */
    fileUploadProgressed: (state, action: PayloadAction<{ loaded: number; total: number }>) => {
      state.fileProgress = action.payload;
    },
    fileParseSucceeded: (state, action: PayloadAction<CsvParseResponse>) => {
      state.fileUpload = applyParseSucceeded(state.fileUpload, action.payload);
      state.fileProgress = null;
    },
    fileParseFailed: (state, action: PayloadAction<AddListError>) => {
      state.fileUpload = applyParseFailed(state.fileUpload);
      state.fileProgress = null;
      state.error = action.payload;
    },
    /** The admin cancelled the upload: back to an empty Upload sub-step. */
    fileParseCancelled: (state) => {
      state.fileUpload = initialFileUploadState;
      state.fileSize = null;
      state.fileProgress = null;
    },
    fileMappingChanged: (state, action: PayloadAction<{ header: string; field: string }>) => {
      state.fileUpload = applyMappingChanged(
        state.fileUpload,
        action.payload.header,
        action.payload.field,
      );
    },
    fileAnalyzeStarted: (state) => {
      state.fileUpload = applyAnalyzeStarted(state.fileUpload);
      state.error = null;
    },
    /** Composes the deferred-import payload, including the CURRENT enriched
     * toggle, so later toggling stays consistent (see fileEnrichedToggled). */
    fileAnalyzeSucceeded: (state, action: PayloadAction<CsvAnalyzeResponse>) => {
      state.fileImportData = {
        columnMapping: state.fileUpload.columnMapping,
        rows: state.fileUpload.parseResult?.rows ?? [],
        analyzeResult: action.payload,
        fileName: state.fileUpload.fileName ?? '',
        isEnriched: state.fileImportData?.isEnriched ?? false,
        // A new analyze lists new conflicts: the previous choices no longer apply.
        overwriteRows: [],
      };
      state.fileUpload = applyAnalyzeSucceeded(state.fileUpload);
    },
    fileAnalyzeFailed: (state, action: PayloadAction<AddListError>) => {
      state.fileUpload = applyAnalyzeFailed(state.fileUpload);
      state.error = action.payload;
    },
    /** The step UI acknowledges the success signal before advancing the wizard. */
    fileAnalyzeAcknowledged: (state) => {
      state.fileUpload = applyAnalyzeAcknowledged(state.fileUpload);
    },
    fileStepBack: (state) => {
      state.fileUpload = applyStepBack(state.fileUpload);
    },
    /** Updates the STORED payload (the old stepper only updated local state,
     * so a toggle after analyze was silently lost; fixed by design here). */
    fileEnrichedToggled: (state, action: PayloadAction<boolean>) => {
      if (state.fileImportData) state.fileImportData.isEnriched = action.payload;
    },
    /** One exit conflict's overwrite, on or off (#709). */
    fileOverwriteRowToggled: (state, action: PayloadAction<number>) => {
      if (state.fileImportData) {
        state.fileImportData.overwriteRows = toggleOverwriteRow(
          state.fileImportData.overwriteRows,
          action.payload,
        );
      }
    },
    enrichNewToggled: (state) => {
      state.enrichNew = !state.enrichNew;
    },
    enrichStaleToggled: (state) => {
      state.enrichStale = !state.enrichStale;
    },

    // ── Filters (delegate to the pure filter-tree domain) ──
    ruleAdded: (state, action: PayloadAction<string | undefined>) => {
      state.filters = addRule(state.filters, action.payload);
      state.rowSelection = EMPTY_SELECTION;
    },
    groupAdded: (
      state,
      action: PayloadAction<{ parentId?: string; combinator?: FilterCombinator } | undefined>,
    ) => {
      state.filters = addGroup(
        state.filters,
        action.payload?.parentId,
        action.payload?.combinator,
      );
      state.rowSelection = EMPTY_SELECTION;
    },
    nodeRemoved: (state, action: PayloadAction<string>) => {
      state.filters = removeNode(state.filters, action.payload);
      state.rowSelection = EMPTY_SELECTION;
    },
    ruleUpdated: (
      state,
      action: PayloadAction<{ id: string; patch: Partial<Omit<FilterRule, 'id' | 'kind'>> }>,
    ) => {
      state.filters = updateRule(state.filters, action.payload.id, action.payload.patch);
      state.rowSelection = EMPTY_SELECTION;
    },
    combinatorSet: (
      state,
      action: PayloadAction<{ groupId: string; combinator: FilterCombinator }>,
    ) => {
      state.filters = setCombinator(state.filters, action.payload.groupId, action.payload.combinator);
      state.rowSelection = EMPTY_SELECTION;
    },
    nodeMoved: (
      state,
      action: PayloadAction<{ id: string; targetParentId: string; targetIndex: number }>,
    ) => {
      const { id, targetParentId, targetIndex } = action.payload;
      state.filters = moveNode(state.filters, id, targetParentId, targetIndex);
      state.rowSelection = EMPTY_SELECTION;
    },
    filtersCleared: (state) => {
      state.filters = clearTree(state.filters);
      state.rowSelection = EMPTY_SELECTION;
    },
    filtersLoaded: (state, action: PayloadAction<FilterGroup>) => {
      state.filters = loadTree(state.filters, action.payload);
      state.rowSelection = EMPTY_SELECTION;
    },

    // ── Profile table (driven by the fetch saga) ──
    pageChanged: (state, action: PayloadAction<number>) => {
      state.profiles.page = action.payload;
    },
    pageSizeChanged: (state, action: PayloadAction<number>) => {
      state.profiles.pageSize = action.payload;
      state.profiles.page = 0;
    },
    profilesLoading: (state) => {
      state.profiles.loading = true;
    },
    profilesLoaded: (
      state,
      action: PayloadAction<{ rows: ProfileResponse[]; total: number }>,
    ) => {
      state.profiles.rows = action.payload.rows;
      state.profiles.total = action.payload.total;
      state.profiles.loading = false;
    },

    // ── Row selection (board E3) ──
    rowToggled: (state, action: PayloadAction<string>) => {
      state.rowSelection = toggleRow(state.rowSelection, action.payload);
    },
    pageSelectionSet: (state, action: PayloadAction<{ ids: string[]; selected: boolean }>) => {
      state.rowSelection = setPageSelected(
        state.rowSelection,
        action.payload.ids,
        action.payload.selected,
      );
    },
    allResultsSelected: (state) => {
      state.rowSelection = selectAllResults();
    },
    rowSelectionCleared: (state) => {
      state.rowSelection = EMPTY_SELECTION;
    },
    /** Restored from the draft; after the filters, whose loading starts it over. */
    rowSelectionHydrated: (state, action: PayloadAction<RowSelection>) => {
      state.rowSelection = action.payload;
    },

    // ── Async results (driven by their workers) ──
    duplicateLoadingStarted: (state) => {
      state.duplicateLoading = true;
    },
    duplicateCountSet: (state, action: PayloadAction<number>) => {
      state.duplicateCount = action.payload;
      state.duplicateLoading = false;
    },
    bucketsLoading: (state) => {
      state.enrichment = EMPTY_ENRICHMENT_STATS;
      state.enrichmentStatus = 'loading';
    },
    bucketsLoaded: (state, action: PayloadAction<EnrichmentStats>) => {
      state.enrichment = action.payload;
      state.enrichmentStatus = 'loaded';
    },
    bucketsFailed: (state) => {
      state.enrichment = EMPTY_ENRICHMENT_STATS;
      state.enrichmentStatus = 'failed';
    },
    nlSearchStarted: (state) => {
      state.nlSearching = true;
    },
    nlSearchEnded: (state) => {
      state.nlSearching = false;
    },

    licencesLoadStarted: (state) => {
      state.licencesLoading = true;
    },
    /** Also the failure transition; a failed load lands an empty dropdown. */
    licencesLoaded: (state, action: PayloadAction<LicenceResponse[]>) => {
      state.licences = action.payload;
      state.licencesLoading = false;
    },

    /** The picked licence's list names arrived (`null` = unknown: no suggestion). */
    licenceListNamesLoaded: (state, action: PayloadAction<string[] | null>) => {
      state.licenceListNames = action.payload;
      refreshNameSuggestion(state);
    },

    /** Reset everything to the initial state (after success / on unmount). */
    resetForm: () => initialState,
  },
});

export const addListActions = slice.actions;
export const addListReducer = slice.reducer;
